import React, { useState, useEffect, useRef } from "react";
import "./AddFareRule.css";
import axios from "axios";
import Switch from "@mui/material/Switch";
import { useNavigate, useSearchParams } from "react-router-dom";
import Loader from "../../Components/Loader/Loader";

const BASE_URL = "https://api.aeroprime.in/crm-service";

const ROUTE_TYPES = ["DOMESTIC", "INTERNATIONAL"];
const DIST_TYPES = ["GDS", "NDC", "LCC"];
const CHARGE_TYPES = ["FIXED", "PERCENTAGE"];
const BAGGAGE_TYPES = ["WEIGHT", "PIECE"];
const BAGGAGE_UNITS = ["KG", "LB", "PC"];
const BAGGAGE_PASSENGERS = [
  { key: "adult", label: "Adult" },
  { key: "child", label: "Child" },
  { key: "infant", label: "Infant" },
];

const emptyChargeSlab = () => ({ timeframe: "", charge_type: "FIXED", value: "", currency: "INR" });
const emptyBaggageAllowance = () => ({ type: "WEIGHT", value: "", unit: "KG", pieces: "" });
const emptyPassengerBaggage = () => ({
  checkin: emptyBaggageAllowance(),
  cabin: emptyBaggageAllowance(),
});
const createDefaultBaggageConfig = () =>
  BAGGAGE_PASSENGERS.reduce((acc, passenger) => {
    acc[passenger.key] = emptyPassengerBaggage();
    return acc;
  }, {});
const normalizeCriteriaValue = (value) => {
  if (Array.isArray(value)) return value.filter((item) => item !== null && item !== undefined && item !== "");
  if (value === null || value === undefined || value === "") return [];
  return [String(value)];
};
const normalizeBaggageAllowance = (allowance = {}) => ({
  type: allowance.type || "WEIGHT",
  value: allowance.value !== null && allowance.value !== undefined ? String(allowance.value) : "",
  unit: allowance.unit || "KG",
  pieces: allowance.pieces !== null && allowance.pieces !== undefined ? String(allowance.pieces) : "",
});
const normalizeBaggageConfig = (baggage) => {
  const normalized = createDefaultBaggageConfig();
  if (!baggage || typeof baggage !== "object") {
    return normalized;
  }

  const hasPassengerSpecificConfig = BAGGAGE_PASSENGERS.some(({ key }) => baggage[key]);

  if (hasPassengerSpecificConfig) {
    BAGGAGE_PASSENGERS.forEach(({ key }) => {
      normalized[key] = {
        checkin: normalizeBaggageAllowance(baggage[key]?.checkin),
        cabin: normalizeBaggageAllowance(baggage[key]?.cabin),
      };
    });
    return normalized;
  }

  BAGGAGE_PASSENGERS.forEach(({ key }) => {
    normalized[key] = {
      checkin: normalizeBaggageAllowance(baggage.checkin),
      cabin: normalizeBaggageAllowance(baggage.cabin),
    };
  });
  return normalized;
};

const AddFareRule = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editRuleId = searchParams.get("ruleId");

  const [showLoader, setShowLoader] = useState(false);

  // Basic fields
  const [ruleId, setRuleId] = useState("");
  const [priority, setPriority] = useState("");
  const [enabled, setEnabled] = useState(true);
  const [effectiveFrom, setEffectiveFrom] = useState("");
  const [effectiveTo, setEffectiveTo] = useState("");

  // Baggage
  const [baggageConfig, setBaggageConfig] = useState(createDefaultBaggageConfig());

  // Fare rule slabs
  const [cancellationSlabs, setCancellationSlabs] = useState([emptyChargeSlab()]);
  const [reissueSlabs, setReissueSlabs] = useState([emptyChargeSlab()]);

  // Inclusion criteria multi-value fields
  const [inclAirlineCode, setInclAirlineCode] = useState([]);
  const [inclAirlineNumber, setInclAirlineNumber] = useState([]);
  const [inclOperatingCarrier, setInclOperatingCarrier] = useState([]);
  const [inclMarketingCarrier, setInclMarketingCarrier] = useState([]);
  const [inclOrigin, setInclOrigin] = useState([]);
  const [inclDestination, setInclDestination] = useState([]);
  const [inclCountryPair, setInclCountryPair] = useState([]);
  const [inclRegionPair, setInclRegionPair] = useState([]);
  const [inclRouteType, setInclRouteType] = useState([]);
  const [inclCabin, setInclCabin] = useState([]);
  const [inclResBookDesigCode, setInclResBookDesigCode] = useState([]);
  const [inclResBookDesigQuantity, setInclResBookDesigQuantity] = useState([]);
  const [inclResBookDesigStatusCode, setInclResBookDesigStatusCode] = useState([]);
  const [inclFareGroupName, setInclFareGroupName] = useState([]);
  const [inclDistType, setInclDistType] = useState([]);

  // Exclusion criteria
  const [exclAirlineCode, setExclAirlineCode] = useState([]);
  const [exclAirlineNumber, setExclAirlineNumber] = useState([]);
  const [exclOperatingCarrier, setExclOperatingCarrier] = useState([]);
  const [exclMarketingCarrier, setExclMarketingCarrier] = useState([]);
  const [exclOrigin, setExclOrigin] = useState([]);
  const [exclDestination, setExclDestination] = useState([]);
  const [exclCountryPair, setExclCountryPair] = useState([]);
  const [exclRegionPair, setExclRegionPair] = useState([]);
  const [exclRouteType, setExclRouteType] = useState([]);
  const [exclCabin, setExclCabin] = useState([]);
  const [exclResBookDesigCode, setExclResBookDesigCode] = useState([]);
  const [exclResBookDesigQuantity, setExclResBookDesigQuantity] = useState([]);
  const [exclResBookDesigStatusCode, setExclResBookDesigStatusCode] = useState([]);
  const [exclFareGroupName, setExclFareGroupName] = useState([]);
  const [exclDistType, setExclDistType] = useState([]);

  const getHeaders = () => ({
    "Content-Type": "application/json",
    Authorization: localStorage.getItem("AuthToken"),
  });

  useEffect(() => {
    if (editRuleId) {
      loadRule(editRuleId);
    }
  }, [editRuleId]);

  const loadRule = (id) => {
    setShowLoader(true);
    axios
      .get(`${BASE_URL}/fareRule/get/${id}`, { headers: getHeaders() })
      .then((res) => {
        const r = res.data;
        setRuleId(r.ruleId || "");
        setPriority(r.priority || "");
        setEnabled(r.enabled !== undefined ? r.enabled : true);
        setEffectiveFrom(r.effectiveFrom || "");
        setEffectiveTo(r.effectiveTo || "");

        if (r.baggage) {
          setBaggageConfig(normalizeBaggageConfig(JSON.parse(r.baggage)));
        }

        if (r.fareRule) {
          const fr = JSON.parse(r.fareRule);
          if (fr.cancellation && fr.cancellation.length > 0) {
            setCancellationSlabs(fr.cancellation);
          }
          if (fr.reissue && fr.reissue.length > 0) {
            setReissueSlabs(fr.reissue);
          }
        }

        if (r.inclusionCriteria) {
          const ic = JSON.parse(r.inclusionCriteria);
          setInclAirlineCode(ic.airline_code || []);
          setInclAirlineNumber(ic.airline_number || []);
          setInclOperatingCarrier(ic.operating_carrier || []);
          setInclMarketingCarrier(ic.marketing_carrier || []);
          setInclOrigin(ic.origin || []);
          setInclDestination(ic.destination || []);
          setInclCountryPair(ic.country_pair || []);
          setInclRegionPair(ic.region_pair || []);
          setInclRouteType(ic.route_type || []);
          setInclCabin(normalizeCriteriaValue(ic.cabin ?? ic.cabin_class));
          setInclResBookDesigCode(normalizeCriteriaValue(ic.resBookDesigCode ?? ic.booking_class));
          setInclResBookDesigQuantity(normalizeCriteriaValue(ic.resBookDesigQuantity));
          setInclResBookDesigStatusCode(normalizeCriteriaValue(ic.resBookDesigStatusCode));
          setInclFareGroupName(normalizeCriteriaValue(ic.fareGroupName ?? ic.fare_family));
          setInclDistType(ic.distribution_type || []);
        }

        if (r.exclusionCriteria) {
          const ec = JSON.parse(r.exclusionCriteria);
          setExclAirlineCode(ec.airline_code || []);
          setExclAirlineNumber(ec.airline_number || []);
          setExclOperatingCarrier(ec.operating_carrier || []);
          setExclMarketingCarrier(ec.marketing_carrier || []);
          setExclOrigin(ec.origin || []);
          setExclDestination(ec.destination || []);
          setExclCountryPair(ec.country_pair || []);
          setExclRegionPair(ec.region_pair || []);
          setExclRouteType(ec.route_type || []);
          setExclCabin(normalizeCriteriaValue(ec.cabin ?? ec.cabin_class));
          setExclResBookDesigCode(normalizeCriteriaValue(ec.resBookDesigCode ?? ec.booking_class));
          setExclResBookDesigQuantity(normalizeCriteriaValue(ec.resBookDesigQuantity));
          setExclResBookDesigStatusCode(normalizeCriteriaValue(ec.resBookDesigStatusCode));
          setExclFareGroupName(normalizeCriteriaValue(ec.fareGroupName ?? ec.fare_family));
          setExclDistType(ec.distribution_type || []);
        }

        setShowLoader(false);
      })
      .catch((err) => {
        setShowLoader(false);
        if (err.response?.status === 401) {
          localStorage.clear();
          window.location.href = "/";
        }
      });
  };

  const buildInclusionCriteria = () => {
    const ic = {};
    if (inclAirlineCode.length) ic.airline_code = inclAirlineCode;
    if (inclAirlineNumber.length) ic.airline_number = inclAirlineNumber;
    if (inclOperatingCarrier.length) ic.operating_carrier = inclOperatingCarrier;
    if (inclMarketingCarrier.length) ic.marketing_carrier = inclMarketingCarrier;
    if (inclOrigin.length) ic.origin = inclOrigin;
    if (inclDestination.length) ic.destination = inclDestination;
    if (inclCountryPair.length) ic.country_pair = inclCountryPair;
    if (inclRegionPair.length) ic.region_pair = inclRegionPair;
    if (inclRouteType.length) ic.route_type = inclRouteType;
    if (inclCabin.length) ic.cabin = inclCabin;
    if (inclResBookDesigCode.length) ic.resBookDesigCode = inclResBookDesigCode;
    if (inclResBookDesigQuantity.length) ic.resBookDesigQuantity = inclResBookDesigQuantity;
    if (inclResBookDesigStatusCode.length) ic.resBookDesigStatusCode = inclResBookDesigStatusCode;
    if (inclFareGroupName.length) ic.fareGroupName = inclFareGroupName;
    if (inclDistType.length) ic.distribution_type = inclDistType;
    return ic;
  };

  const buildExclusionCriteria = () => {
    const ec = {};
    if (exclAirlineCode.length) ec.airline_code = exclAirlineCode;
    if (exclAirlineNumber.length) ec.airline_number = exclAirlineNumber;
    if (exclOperatingCarrier.length) ec.operating_carrier = exclOperatingCarrier;
    if (exclMarketingCarrier.length) ec.marketing_carrier = exclMarketingCarrier;
    if (exclOrigin.length) ec.origin = exclOrigin;
    if (exclDestination.length) ec.destination = exclDestination;
    if (exclCountryPair.length) ec.country_pair = exclCountryPair;
    if (exclRegionPair.length) ec.region_pair = exclRegionPair;
    if (exclRouteType.length) ec.route_type = exclRouteType;
    if (exclCabin.length) ec.cabin = exclCabin;
    if (exclResBookDesigCode.length) ec.resBookDesigCode = exclResBookDesigCode;
    if (exclResBookDesigQuantity.length) ec.resBookDesigQuantity = exclResBookDesigQuantity;
    if (exclResBookDesigStatusCode.length) ec.resBookDesigStatusCode = exclResBookDesigStatusCode;
    if (exclFareGroupName.length) ec.fareGroupName = exclFareGroupName;
    if (exclDistType.length) ec.distribution_type = exclDistType;
    return ec;
  };

  const updateBaggageField = (passengerKey, baggageType, field, value) => {
    setBaggageConfig((prev) => ({
      ...prev,
      [passengerKey]: {
        ...prev[passengerKey],
        [baggageType]: {
          ...prev[passengerKey][baggageType],
          [field]: value,
        },
      },
    }));
  };

  const handleSave = () => {
    if (!ruleId.trim()) {
      alert("Rule ID is required.");
      return;
    }
    if (!priority || isNaN(parseInt(priority))) {
      alert("Priority is required and must be a number.");
      return;
    }

    const validCancellation = cancellationSlabs.filter((s) => s.timeframe);
    const validReissue = reissueSlabs.filter((s) => s.timeframe);

    const payload = {
      rule_id: ruleId.trim(),
      priority: parseInt(priority),
      enabled: enabled,
      effective_from: effectiveFrom || null,
      effective_to: effectiveTo || null,
      inclusion_criteria: buildInclusionCriteria(),
      exclusion_criteria: buildExclusionCriteria(),
      baggage: BAGGAGE_PASSENGERS.reduce((acc, passenger) => {
        const passengerBaggage = baggageConfig[passenger.key] || emptyPassengerBaggage();
        acc[passenger.key] = {
          checkin: {
            type: passengerBaggage.checkin.type,
            value: passengerBaggage.checkin.value !== "" ? parseFloat(passengerBaggage.checkin.value) : null,
            unit: passengerBaggage.checkin.unit,
            pieces: passengerBaggage.checkin.pieces !== "" ? parseInt(passengerBaggage.checkin.pieces, 10) : null,
          },
          cabin: {
            type: passengerBaggage.cabin.type,
            value: passengerBaggage.cabin.value !== "" ? parseFloat(passengerBaggage.cabin.value) : null,
            unit: passengerBaggage.cabin.unit,
            pieces: passengerBaggage.cabin.pieces !== "" ? parseInt(passengerBaggage.cabin.pieces, 10) : null,
          },
        };
        return acc;
      }, {}),
      fare_rule: {
        cancellation: validCancellation.map((s) => {
          const entry = { timeframe: s.timeframe };
          if (s.value !== "") {
            entry.charge_type = s.charge_type;
            entry.value = parseFloat(s.value);
            entry.currency = s.currency;
          }
          return entry;
        }),
        reissue: validReissue.map((s) => {
          const entry = { timeframe: s.timeframe };
          if (s.value !== "") {
            entry.charge_type = s.charge_type;
            entry.value = parseFloat(s.value);
            entry.currency = s.currency;
          }
          return entry;
        }),
      },
      updated_by: localStorage.getItem("userName") || "admin",
    };

    setShowLoader(true);
    axios
      .post(`${BASE_URL}/fareRule/saveUpdate`, payload, { headers: getHeaders() })
      .then((res) => {
        setShowLoader(false);
        if (res.data?.success) {
          navigate("/fareRules");
        } else {
          alert("Failed to save: " + (res.data?.message || "Unknown error"));
        }
      })
      .catch((err) => {
        setShowLoader(false);
        if (err.response?.status === 401) {
          localStorage.clear();
          window.location.href = "/";
        } else {
          alert("Failed to save rule.");
        }
      });
  };

  const updateSlab = (list, setList, index, field, value) => {
    const updated = list.map((s, i) => (i === index ? { ...s, [field]: value } : s));
    setList(updated);
  };

  const removeSlab = (list, setList, index) => {
    setList(list.filter((_, i) => i !== index));
  };

  return (
    <div className="add-fare-rule-wrapper">
      {showLoader && <Loader hideLoader={false} />}
      <div className="add-fare-rule-heading">
        {editRuleId ? "EDIT FARE RULE" : "ADD FARE RULE"}
      </div>
      <div className="Break-line-rp"></div>

      {/* Basic Info */}
      <div className="form-section">
        <div className="form-section-title">Basic Information</div>
        <div className="form-row">
          <div className="form-field">
            <label>Rule ID *</label>
            <input
              value={ruleId}
              onChange={(e) => setRuleId(e.target.value)}
              placeholder="e.g. EK_INTL_ECO"
              disabled={!!editRuleId}
            />
          </div>
          <div className="form-field">
            <label>Priority *</label>
            <input
              type="number"
              value={priority}
              onChange={(e) => setPriority(e.target.value)}
              placeholder="e.g. 800"
            />
          </div>
          <div className="form-field">
            <label>Effective From</label>
            <input
              type="date"
              value={effectiveFrom}
              onChange={(e) => setEffectiveFrom(e.target.value)}
            />
          </div>
          <div className="form-field">
            <label>Effective To</label>
            <input
              type="date"
              value={effectiveTo}
              onChange={(e) => setEffectiveTo(e.target.value)}
            />
          </div>
          <div className="form-field">
            <label>Enabled</label>
            <div className="toggle-switch">
              <Switch
                checked={enabled}
                onChange={(e) => setEnabled(e.target.checked)}
                sx={{ "& .MuiSwitch-switchBase.Mui-checked": { color: "#ef5443" }, "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#ef5443" } }}
              />
              <span className="toggle-label">{enabled ? "Active" : "Inactive"}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Baggage */}
      <div className="form-section">
        <div className="form-section-title">Baggage Configuration</div>
        <div className="baggage-config-grid">
          {BAGGAGE_PASSENGERS.map((passenger) => (
            <div className="baggage-config-card" key={passenger.key}>
              <div className="baggage-card-title">{passenger.label}</div>
              <div className="baggage-subtitle">Check-in Baggage</div>
              <div className="form-row">
                <div className="form-field">
                  <label>Type</label>
                  <select
                    value={baggageConfig[passenger.key]?.checkin?.type || "WEIGHT"}
                    onChange={(e) => updateBaggageField(passenger.key, "checkin", "type", e.target.value)}
                  >
                    {BAGGAGE_TYPES.map((t) => <option key={t}>{t}</option>)}
                  </select>
                </div>
                <div className="form-field">
                  <label>Value</label>
                  <input
                    type="number"
                    value={baggageConfig[passenger.key]?.checkin?.value || ""}
                    onChange={(e) => updateBaggageField(passenger.key, "checkin", "value", e.target.value)}
                    placeholder="e.g. 30"
                  />
                </div>
                <div className="form-field">
                  <label>Unit</label>
                  <select
                    value={baggageConfig[passenger.key]?.checkin?.unit || "KG"}
                    onChange={(e) => updateBaggageField(passenger.key, "checkin", "unit", e.target.value)}
                  >
                    {BAGGAGE_UNITS.map((u) => <option key={u}>{u}</option>)}
                  </select>
                </div>
                {baggageConfig[passenger.key]?.checkin?.type === "WEIGHT" && (
                  <div className="form-field">
                    <label>Pieces</label>
                    <input
                      type="number"
                      min="1"
                      value={baggageConfig[passenger.key]?.checkin?.pieces || ""}
                      onChange={(e) => updateBaggageField(passenger.key, "checkin", "pieces", e.target.value)}
                      placeholder="e.g. 2"
                    />
                  </div>
                )}
              </div>
              <div className="baggage-subtitle">Cabin Baggage</div>
              <div className="form-row">
                <div className="form-field">
                  <label>Type</label>
                  <select
                    value={baggageConfig[passenger.key]?.cabin?.type || "WEIGHT"}
                    onChange={(e) => updateBaggageField(passenger.key, "cabin", "type", e.target.value)}
                  >
                    {BAGGAGE_TYPES.map((t) => <option key={t}>{t}</option>)}
                  </select>
                </div>
                <div className="form-field">
                  <label>Value</label>
                  <input
                    type="number"
                    value={baggageConfig[passenger.key]?.cabin?.value || ""}
                    onChange={(e) => updateBaggageField(passenger.key, "cabin", "value", e.target.value)}
                    placeholder="e.g. 7"
                  />
                </div>
                <div className="form-field">
                  <label>Unit</label>
                  <select
                    value={baggageConfig[passenger.key]?.cabin?.unit || "KG"}
                    onChange={(e) => updateBaggageField(passenger.key, "cabin", "unit", e.target.value)}
                  >
                    {BAGGAGE_UNITS.map((u) => <option key={u}>{u}</option>)}
                  </select>
                </div>
                {baggageConfig[passenger.key]?.cabin?.type === "WEIGHT" && (
                  <div className="form-field">
                    <label>Pieces</label>
                    <input
                      type="number"
                      min="1"
                      value={baggageConfig[passenger.key]?.cabin?.pieces || ""}
                      onChange={(e) => updateBaggageField(passenger.key, "cabin", "pieces", e.target.value)}
                      placeholder="e.g. 2"
                    />
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Cancellation Charges */}
      <div className="form-section">
        <div className="form-section-title">Cancellation Charges</div>
        {cancellationSlabs.map((slab, i) => (
          <div className="slab-row" key={i}>
            <input
              placeholder="Timeframe (e.g. 0-24 hours before departure)"
              value={slab.timeframe}
              onChange={(e) => updateSlab(cancellationSlabs, setCancellationSlabs, i, "timeframe", e.target.value)}
              style={{ flex: 3 }}
            />
            <select
              value={slab.charge_type}
              onChange={(e) => updateSlab(cancellationSlabs, setCancellationSlabs, i, "charge_type", e.target.value)}
              disabled={slab.value === ""}
              style={{ opacity: slab.value === "" ? 0.4 : 1 }}
            >
              {CHARGE_TYPES.map((c) => <option key={c}>{c}</option>)}
            </select>
            <input
              type="number"
              placeholder="Amount"
              value={slab.value}
              onChange={(e) => updateSlab(cancellationSlabs, setCancellationSlabs, i, "value", e.target.value)}
              style={{ flex: 1 }}
            />
            <input
              placeholder={slab.value === "" ? "N/A" : "Currency"}
              value={slab.value === "" ? "" : slab.currency}
              onChange={(e) => updateSlab(cancellationSlabs, setCancellationSlabs, i, "currency", e.target.value)}
              disabled={slab.value === ""}
              style={{ flex: 1, opacity: slab.value === "" ? 0.4 : 1 }}
            />
            <button className="remove-slab-btn" onClick={() => removeSlab(cancellationSlabs, setCancellationSlabs, i)}>×</button>
          </div>
        ))}
        <button className="add-slab-btn" onClick={() => setCancellationSlabs([...cancellationSlabs, emptyChargeSlab()])}>
          + Add Slab
        </button>
      </div>

      {/* Reissue Charges */}
      <div className="form-section">
        <div className="form-section-title">Reissue Charges</div>
        {reissueSlabs.map((slab, i) => (
          <div className="slab-row" key={i}>
            <input
              placeholder="Timeframe (e.g. 0-24 hours before departure)"
              value={slab.timeframe}
              onChange={(e) => updateSlab(reissueSlabs, setReissueSlabs, i, "timeframe", e.target.value)}
              style={{ flex: 3 }}
            />
            <select
              value={slab.charge_type}
              onChange={(e) => updateSlab(reissueSlabs, setReissueSlabs, i, "charge_type", e.target.value)}
              disabled={slab.value === ""}
              style={{ opacity: slab.value === "" ? 0.4 : 1 }}
            >
              {CHARGE_TYPES.map((c) => <option key={c}>{c}</option>)}
            </select>
            <input
              type="number"
              placeholder="Amount"
              value={slab.value}
              onChange={(e) => updateSlab(reissueSlabs, setReissueSlabs, i, "value", e.target.value)}
              style={{ flex: 1 }}
            />
            <input
              placeholder={slab.value === "" ? "N/A" : "Currency"}
              value={slab.value === "" ? "" : slab.currency}
              onChange={(e) => updateSlab(reissueSlabs, setReissueSlabs, i, "currency", e.target.value)}
              disabled={slab.value === ""}
              style={{ flex: 1, opacity: slab.value === "" ? 0.4 : 1 }}
            />
            <button className="remove-slab-btn" onClick={() => removeSlab(reissueSlabs, setReissueSlabs, i)}>×</button>
          </div>
        ))}
        <button className="add-slab-btn" onClick={() => setReissueSlabs([...reissueSlabs, emptyChargeSlab()])}>
          + Add Slab
        </button>
      </div>

      {/* Inclusion Criteria */}
      <div className="form-section">
        <div className="form-section-title">Inclusion Criteria</div>
        <CriteriaFields
          routeTypes={ROUTE_TYPES}
          distTypes={DIST_TYPES}
          airlineCode={inclAirlineCode} setAirlineCode={setInclAirlineCode}
          airlineNumber={inclAirlineNumber} setAirlineNumber={setInclAirlineNumber}
          operatingCarrier={inclOperatingCarrier} setOperatingCarrier={setInclOperatingCarrier}
          marketingCarrier={inclMarketingCarrier} setMarketingCarrier={setInclMarketingCarrier}
          origin={inclOrigin} setOrigin={setInclOrigin}
          destination={inclDestination} setDestination={setInclDestination}
          countryPair={inclCountryPair} setCountryPair={setInclCountryPair}
          regionPair={inclRegionPair} setRegionPair={setInclRegionPair}
          routeType={inclRouteType} setRouteType={setInclRouteType}
          cabin={inclCabin} setCabin={setInclCabin}
          resBookDesigCode={inclResBookDesigCode} setResBookDesigCode={setInclResBookDesigCode}
          resBookDesigQuantity={inclResBookDesigQuantity} setResBookDesigQuantity={setInclResBookDesigQuantity}
          resBookDesigStatusCode={inclResBookDesigStatusCode} setResBookDesigStatusCode={setInclResBookDesigStatusCode}
          fareGroupName={inclFareGroupName} setFareGroupName={setInclFareGroupName}
          distType={inclDistType} setDistType={setInclDistType}
        />
      </div>

      {/* Exclusion Criteria */}
      <div className="form-section">
        <div className="form-section-title">Exclusion Criteria</div>
        <CriteriaFields
          routeTypes={ROUTE_TYPES}
          distTypes={DIST_TYPES}
          airlineCode={exclAirlineCode} setAirlineCode={setExclAirlineCode}
          airlineNumber={exclAirlineNumber} setAirlineNumber={setExclAirlineNumber}
          operatingCarrier={exclOperatingCarrier} setOperatingCarrier={setExclOperatingCarrier}
          marketingCarrier={exclMarketingCarrier} setMarketingCarrier={setExclMarketingCarrier}
          origin={exclOrigin} setOrigin={setExclOrigin}
          destination={exclDestination} setDestination={setExclDestination}
          countryPair={exclCountryPair} setCountryPair={setExclCountryPair}
          regionPair={exclRegionPair} setRegionPair={setExclRegionPair}
          routeType={exclRouteType} setRouteType={setExclRouteType}
          cabin={exclCabin} setCabin={setExclCabin}
          resBookDesigCode={exclResBookDesigCode} setResBookDesigCode={setExclResBookDesigCode}
          resBookDesigQuantity={exclResBookDesigQuantity} setResBookDesigQuantity={setExclResBookDesigQuantity}
          resBookDesigStatusCode={exclResBookDesigStatusCode} setResBookDesigStatusCode={setExclResBookDesigStatusCode}
          fareGroupName={exclFareGroupName} setFareGroupName={setExclFareGroupName}
          distType={exclDistType} setDistType={setExclDistType}
        />
      </div>

      <div className="form-actions">
        <button className="cancel-btn" onClick={() => navigate("/fareRules")}>Cancel</button>
        <button className="save-btn" onClick={handleSave}>Save Rule</button>
      </div>
    </div>
  );
};

// Reusable chip input component
const ChipInput = ({ values, onChange, placeholder, options, normalizeValue = (val) => val.trim().toUpperCase() }) => {
  const [inputVal, setInputVal] = useState("");
  const inputRef = useRef(null);

  const addValue = (val) => {
    const trimmed = normalizeValue(val);
    if (trimmed && !values.includes(trimmed)) {
      onChange([...values, trimmed]);
    }
    setInputVal("");
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addValue(inputVal);
    } else if (e.key === "Backspace" && !inputVal && values.length > 0) {
      onChange(values.slice(0, -1));
    }
  };

  const handleSelectChange = (e) => {
    const val = e.target.value;
    if (val && !values.includes(val)) {
      onChange([...values, val]);
    }
    e.target.value = "";
  };

  return (
    <div className="chip-input-area" onClick={() => inputRef.current?.focus()}>
      {values.map((v) => (
        <span key={v} className="chip">
          {v}
          <span className="chip-remove" onClick={(e) => { e.stopPropagation(); onChange(values.filter((x) => x !== v)); }}>×</span>
        </span>
      ))}
      {options ? (
        <select onChange={handleSelectChange} style={{ border: "none", outline: "none", fontSize: 13, flex: 1, minWidth: 80, background: "transparent" }}>
          <option value="">{placeholder}</option>
          {options.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      ) : (
        <input
          ref={inputRef}
          className="chip-input"
          value={inputVal}
          onChange={(e) => setInputVal(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={() => { if (inputVal) addValue(inputVal); }}
          placeholder={values.length === 0 ? placeholder : ""}
        />
      )}
    </div>
  );
};

// Criteria fields used by both inclusion and exclusion
const CriteriaFields = ({
  routeTypes, distTypes,
  airlineCode, setAirlineCode,
  airlineNumber, setAirlineNumber,
  operatingCarrier, setOperatingCarrier,
  marketingCarrier, setMarketingCarrier,
  origin, setOrigin,
  destination, setDestination,
  countryPair, setCountryPair,
  regionPair, setRegionPair,
  routeType, setRouteType,
  cabin, setCabin,
  resBookDesigCode, setResBookDesigCode,
  resBookDesigQuantity, setResBookDesigQuantity,
  resBookDesigStatusCode, setResBookDesigStatusCode,
  fareGroupName, setFareGroupName,
  distType, setDistType,
}) => (
  <>
    <div className="form-row">
      <div className="form-field">
        <label>Airline Code</label>
        <ChipInput values={airlineCode} onChange={setAirlineCode} placeholder="Type & Enter (e.g. EK)" />
      </div>
      <div className="form-field">
        <label>Airline Number</label>
        <ChipInput values={airlineNumber} onChange={setAirlineNumber} placeholder="Type & Enter (e.g. 176)" />
      </div>
      <div className="form-field">
        <label>Operating Carrier</label>
        <ChipInput values={operatingCarrier} onChange={setOperatingCarrier} placeholder="Type & Enter" />
      </div>
      <div className="form-field">
        <label>Marketing Carrier</label>
        <ChipInput values={marketingCarrier} onChange={setMarketingCarrier} placeholder="Type & Enter" />
      </div>
    </div>
    <div className="form-row">
      <div className="form-field">
        <label>Origin</label>
        <ChipInput values={origin} onChange={setOrigin} placeholder="Type & Enter (e.g. DEL)" />
      </div>
      <div className="form-field">
        <label>Destination</label>
        <ChipInput values={destination} onChange={setDestination} placeholder="Type & Enter (e.g. DXB)" />
      </div>
      <div className="form-field">
        <label>Country Pair</label>
        <ChipInput values={countryPair} onChange={setCountryPair} placeholder="Type & Enter (e.g. IN-AE)" />
      </div>
      <div className="form-field">
        <label>Region Pair</label>
        <ChipInput values={regionPair} onChange={setRegionPair} placeholder="Type & Enter" />
      </div>
    </div>
    <div className="form-row">
      <div className="form-field">
        <label>Route Type</label>
        <ChipInput values={routeType} onChange={setRouteType} placeholder="Select" options={routeTypes} />
      </div>
      <div className="form-field">
        <label>Cabin</label>
        <ChipInput
          values={cabin}
          onChange={setCabin}
          placeholder="Type & Enter (e.g. Economy)"
          normalizeValue={(val) => val.trim()}
        />
      </div>
      <div className="form-field">
        <label>RBD Code</label>
        <ChipInput values={resBookDesigCode} onChange={setResBookDesigCode} placeholder="Type & Enter (e.g. Y)" />
      </div>
      <div className="form-field">
        <label>RBD Quantity</label>
        <ChipInput
          values={resBookDesigQuantity}
          onChange={setResBookDesigQuantity}
          placeholder="Type & Enter (e.g. 9)"
          normalizeValue={(val) => val.trim()}
        />
      </div>
    </div>
    <div className="form-row">
      <div className="form-field">
        <label>RBD Status Code</label>
        <ChipInput values={resBookDesigStatusCode} onChange={setResBookDesigStatusCode} placeholder="Type & Enter (e.g. Y)" />
      </div>
      <div className="form-field">
        <label>Fare Group Name</label>
        <ChipInput
          values={fareGroupName}
          onChange={setFareGroupName}
          placeholder="Type & Enter (e.g. Itin_0)"
          normalizeValue={(val) => val.trim()}
        />
      </div>
      <div className="form-field">
        <label>Distribution Type</label>
        <ChipInput values={distType} onChange={setDistType} placeholder="Select" options={distTypes} />
      </div>
    </div>
  </>
);

export default AddFareRule;
